import type { CapsuleProject } from "@app/interfaces/CapsuleProject";
import { ProjectSummary } from "@app/interfaces/ProjectSummary";
import { Scope } from "@sentry/react";
import axios, { isAxiosError } from "axios";
import {
	hoursAllowedTag,
	hoursInDay,
	minutesRemainingTag,
	minutesUsedTag,
} from "./CapsuleProjects";

export function API() {
	const instance = axios.create({
		baseURL:
			import.meta.env.VITE_BASE_URL ??
			"https://timesheet.chartis-internal.net/",
		timeout: 50000,
		params: {},
	});

	// Intercept responses to handle redirects to login
	instance.interceptors.response.use(
		(response) => response,
		(error) => {
			// If unauthorized or server wants us to login
			if (error.response?.status === 401 || error.response?.status === 302) {
				window.location.href = "/auth/login";
			}
			return Promise.reject(error);
		},
	);

	return instance;
}

export type NormalizedApiError = {
	isAxiosError: boolean;
	status?: number;
	data?: unknown;
	apiPath: string;
	method: string;
	name: string;
	message: string;
	fallbackThrownValue?: unknown;
	captureError: Error;
};

export function NormalizeApiError(error: unknown): NormalizedApiError {
	const axiosErr = isAxiosError(error) ? error : undefined;
	const status = axiosErr?.response?.status;
	const data = axiosErr?.response?.data;
	const apiPath = axiosErr?.config?.url ?? "unknown";
	const method = axiosErr?.config?.method?.toUpperCase() ?? "unknown";
	const fallbackName = error instanceof Error ? error.name : "UnknownError";
	const fallbackMessage =
		error instanceof Error ? error.message : "Non-Error value thrown";
	const captureError =
		axiosErr ??
		(error instanceof Error ? error : new Error("Non-Error value thrown"));

	return {
		isAxiosError: axiosErr != null,
		status,
		data,
		apiPath,
		method,
		name: axiosErr?.name ?? fallbackName,
		message: axiosErr?.message ?? fallbackMessage,
		fallbackThrownValue: axiosErr == null ? error : undefined,
		captureError,
	};
}

export function setSentryErrorTags(scope: Scope, apiErr: NormalizedApiError) {
	scope.setTag("api", apiErr.apiPath);
	scope.setTag("http_method", apiErr.method);
	scope.setTag("is_axios_error", apiErr.isAxiosError.toString());

	if (apiErr.status != null) {
		scope.setTag("http_status", apiErr.status.toFixed(0));
	}

	scope.setExtra("error", {
		message: apiErr.message,
		name: apiErr.name,
		status: apiErr.status,
		method: apiErr.method,
		apiPath: apiErr.apiPath,
		data: apiErr.data,
		fallbackThrownValue: apiErr.fallbackThrownValue,
	});
}

// Overload typescript definition for when a forceDefault is supplied (to ensure non-undefined response)
export function ParseCapsuleProjectResponse(
	r: CapsuleProject,
	forceDefaults: ProjectSummary,
): ProjectSummary;

// Overload typescript definition for when a forceDefault is not supplied (allowing for undefined response)
export function ParseCapsuleProjectResponse(
	r: CapsuleProject,
): ProjectSummary | undefined;

// Implementation of the ParseCapsuleProjectResponse function
export function ParseCapsuleProjectResponse(
	r: CapsuleProject,
	forceDefaults?: ProjectSummary,
) {
	const allocatedHours = r.fields.find(
		(f) => f.definition.id === hoursAllowedTag,
	);
	const usedMinutes = r.fields.find(
		(f) => f.definition.id === minutesUsedTag,
	) ?? { value: 0, id: 0 };
	const remainingMinutes = r.fields.find(
		(f) => f.definition.id === minutesRemainingTag,
	) ?? { value: 0, id: 0 };

	// If the Capsule Project has not been set up with the ALLOCATED HOURS tag, we ignore it.
	if (allocatedHours != null) {
		const summary: ProjectSummary = {
			id: r.id,
			project_id: r.name,
			project_reference: r.description,
			capsule_id: r.party.id,
			org_name: r.party.name,
			minutes_worked: Number(usedMinutes.value),
			minutes_worked_id: usedMinutes.id,
			allocated_days: Number((allocatedHours.value / hoursInDay).toFixed(2)),
			allocated_days_id: allocatedHours.id,
			minutes_remaining: Number(remainingMinutes.value),
			minutes_remaining_id: remainingMinutes.id,
			project_status: r.status,
		};
		return summary;
	} else if (forceDefaults != null) {
		const summary: ProjectSummary = {
			id: r.id,
			project_id: r.name,
			project_reference: r.description,
			capsule_id: r.party.id,
			org_name: r.party.name,
			minutes_worked: Number(usedMinutes.value),
			minutes_worked_id: usedMinutes.id,
			allocated_days: forceDefaults.allocated_days,
			allocated_days_id: forceDefaults.allocated_days_id,
			minutes_remaining: Number(remainingMinutes.value),
			minutes_remaining_id: remainingMinutes.id,
			project_status: r.status,
		};
		return summary;
	} else {
		console.warn(
			"Project " +
				r.name +
				" (" +
				r.id +
				") does not have allocated hours set - ignoring",
		);
	}
}
